
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, CreditCard, Calendar, ArrowRight } from 'lucide-react';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/sonner';
import { updateSubscription } from '@/pages/admin/rentals/services/subscriptionUpdate';
import { Subscription, SubscriptionUpdate } from '@/services/types/subscriptionTypes';
import { calculateNextPaymentDate, formatDateForApi } from '@/utils/dateUtils';
import { ScrollArea } from '@/components/ui/scroll-area';

// Схема валидации формы редактирования подписки
const formSchema = z.object({
  amount: z.coerce.number().min(1, { message: 'Сумма должна быть больше 0' }),
  interval: z.enum(['day', 'week', 'month', 'year'], {
    required_error: 'Выберите периодичность',
  }),
  next_payment_date: z.string().optional(),
  card_first6: z.string().max(6).optional(),
  card_last4: z.string().max(4).optional(),
  card_expiry_month: z.string().max(2).optional(),
  card_expiry_year: z.string().max(4).optional(),
  card_type: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface EditSubscriptionFormProps {
  subscription: Subscription;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubscriptionUpdated: () => void;
}

export const EditSubscriptionForm: React.FC<EditSubscriptionFormProps> = ({
  subscription,
  open,
  onOpenChange,
  onSubscriptionUpdated
}) => {
  const [loading, setLoading] = useState(false);
  
  // Инициализация формы с данными подписки
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      amount: subscription.amount,
      interval: subscription.interval,
      next_payment_date: subscription.next_payment_date ? 
        formatDateForApi(new Date(subscription.next_payment_date)) : 
        undefined,
      card_first6: subscription.card_first6,
      card_last4: subscription.card_last4,
      card_expiry_month: subscription.card_expiry_month,
      card_expiry_year: subscription.card_expiry_year,
      card_type: subscription.card_type,
    },
  });
  
  // Функция пересчета даты следующего платежа при изменении интервала
  const recalculateNextPaymentDate = (interval: 'day' | 'week' | 'month' | 'year') => {
    const nextDate = calculateNextPaymentDate(new Date(), interval);
    form.setValue('next_payment_date', formatDateForApi(nextDate));
  };
  
  // Обработчик отправки формы
  async function onSubmit(values: FormValues) {
    setLoading(true);
    
    try {
      // Подготавливаем данные для обновления
      const updateData: SubscriptionUpdate = {
        id: subscription.id,
        amount: values.amount,
        interval: values.interval,
        next_payment_date: values.next_payment_date,
      };
      
      // Добавляем данные карты, только если они указаны
      if (values.card_first6) updateData.card_first6 = values.card_first6;
      if (values.card_last4) updateData.card_last4 = values.card_last4;
      if (values.card_expiry_month) updateData.card_expiry_month = values.card_expiry_month;
      if (values.card_expiry_year) updateData.card_expiry_year = values.card_expiry_year;
      if (values.card_type) updateData.card_type = values.card_type;
      
      console.log('Обновляем подписку:', updateData);
      
      const { success, error } = await updateSubscription(updateData);
      
      if (success) {
        toast.success('Подписка успешно обновлена');
        onOpenChange(false);
        onSubscriptionUpdated();
      } else {
        toast.error(`Ошибка при обновлении подписки: ${error}`);
      }
    } catch (err: any) {
      console.error('Ошибка при обновлении подписки:', err);
      toast.error('Произошла ошибка при обновлении подписки');
    } finally {
      setLoading(false);
    }
  }
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle>Редактирование подписки</DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[calc(90vh-150px)]">
          <div className="p-6 pt-2">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Сумма платежа (₽)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="Укажите сумму" 
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="interval"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Периодичность платежа</FormLabel>
                      <Select 
                        onValueChange={(value) => {
                          field.onChange(value);
                          recalculateNextPaymentDate(value as 'day' | 'week' | 'month' | 'year');
                        }} 
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Выберите периодичность" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="day">Ежедневно</SelectItem>
                          <SelectItem value="week">Еженедельно</SelectItem>
                          <SelectItem value="month">Ежемесячно</SelectItem>
                          <SelectItem value="year">Ежегодно</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="next_payment_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" /> 
                        Дата следующего платежа
                      </FormLabel>
                      <FormControl>
                        <Input 
                          type="date" 
                          {...field} 
                          value={field.value || ''} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="bg-muted/30 p-3 rounded-lg border">
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
                    <CreditCard className="h-4 w-4" /> 
                    Данные платежной карты (опционально)
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <FormField
                      control={form.control}
                      name="card_first6"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Первые 6 цифр</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="XXXXXX" 
                              maxLength={6}
                              {...field} 
                              value={field.value || ''} 
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="card_last4"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Последние 4 цифры</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="XXXX" 
                              maxLength={4}
                              {...field} 
                              value={field.value || ''} 
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <FormField
                      control={form.control}
                      name="card_expiry_month"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Месяц</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="MM" 
                              maxLength={2}
                              {...field} 
                              value={field.value || ''} 
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="card_expiry_year"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Год</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="YYYY" 
                              maxLength={4}
                              {...field} 
                              value={field.value || ''} 
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="card_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Тип карты</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value || ''}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Тип" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="none">Не указан</SelectItem>
                              <SelectItem value="Visa">Visa</SelectItem>
                              <SelectItem value="MasterCard">MasterCard</SelectItem>
                              <SelectItem value="Mir">Мир</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </form>
            </Form>
          </div>
        </ScrollArea>
        
        <DialogFooter className="p-6 pt-2 border-t">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Отмена
          </Button>
          <Button 
            onClick={form.handleSubmit(onSubmit)} 
            disabled={loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
